import React from "react";
import { WifiOff, RefreshCw } from "lucide-react";
import Card from "./Card";
import Button from "./Button";

/**
 * OfflineUnavailable — single component for "we can't load this surface
 * because the device is offline AND no local snapshot exists yet."
 *
 * Critical rule: this is NOT the same as the empty-data state.
 *   - "0 batches" / "No items yet" → genuine empty data (server replied).
 *   - <OfflineUnavailable />        → we don't know; reconnect to find out.
 *
 *   <OfflineUnavailable
 *     title="Inventory unavailable offline"
 *     description="This device does not currently have a local inventory snapshot. Reconnect to refresh inventory."
 *     onRetry={loadFn}
 *   />
 */
export default function OfflineUnavailable({
  title,
  description,
  onRetry,
  className = "",
  "data-testid": testId = "offline-unavailable",
}) {
  return (
    <Card
      variant="raised"
      className={`p-8 flex flex-col items-center text-center ${className}`}
      data-testid={testId}
    >
      <div className="h-12 w-12 rounded-chip bg-paper-sunk flex items-center justify-center mb-3 text-ink-2">
        <WifiOff size={22} strokeWidth={2} />
      </div>
      <h3 className="font-display font-bold text-base text-ink mb-2">
        {title}
      </h3>
      {description && (
        <p className="text-sm text-ink-2 max-w-sm leading-relaxed">
          {description}
        </p>
      )}
      {onRetry && (
        <Button
          tier="secondary"
          onClick={onRetry}
          className="mt-4"
          data-testid="offline-unavailable-retry"
        >
          <RefreshCw size={14} /> Try again
        </Button>
      )}
    </Card>
  );
}

/**
 * CachedDataNotice — slim inline notice rendered above a list when the
 * data shown is a local IndexedDB snapshot rather than fresh from the
 * server.
 *
 *   <CachedDataNotice fetchedAt={cached.fetchedAt} />
 */
export function CachedDataNotice({
  fetchedAt,
  label = "Showing cached data",
  className = "",
  "data-testid": testId = "cached-data-notice",
}) {
  let rel = "";
  try {
    if (fetchedAt) {
      const sec = Math.max(0, Math.floor((Date.now() - fetchedAt) / 1000));
      if (sec < 60)       rel = "just now";
      else if (sec < 3600) rel = `${Math.floor(sec / 60)} min ago`;
      else if (sec < 86400) rel = `${Math.floor(sec / 3600)} hr ago`;
      else rel = new Date(fetchedAt).toLocaleString();
    }
  } catch (_) { /* noop */ }
  return (
    <div
      className={`mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-chip bg-paper-sunk text-ink-2 ${className}`}
      data-testid={testId}
      role="status"
    >
      <WifiOff size={12} strokeWidth={2.5} />
      <span className="font-mono text-[11px] uppercase tracking-wider">{label}</span>
      {rel && (
        <span className="font-mono text-[11px] text-ink-3">· {rel}</span>
      )}
    </div>
  );
}
